import { authen } from '../firebase/firebase';

interface UserProfileData {
  auth_type?: string;
  channel?: string;
  free_trial_expiry?: string | number;
  membership_end_date?: string | number | any; 
  user_role?: string;
  school_id?: string;
  district_id?: string;
  teacher_id?: string;
  tenant_id?: string;
  source_id?: string;
  metrics?: {
    words_num_total?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

interface TrialExpiryResult {
  expired: boolean;
  message: string;
  redirectToMembership: boolean;
  isPremium?: boolean;
}

const parseDate = (dateValue: string | number | any): Date | null => {
  if (!dateValue) return null;
  
  try {
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      console.log('[parseDate] Converting Firestore Timestamp to Date');
      return new Date(dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000);
    }
    
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      console.log('[parseDate] Invalid date format:', dateValue);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('[parseDate] Error parsing date:', error, 'Value:', dateValue);
    return null;
  }
};

const isInstitutionalAccount = (authType: string | undefined): boolean => {
  if (!authType) return false;
  
  const SSO_AUTH_TYPES = ['username', 'classlink-sso', 'clever-library', 'district-sso'];
  return SSO_AUTH_TYPES.includes(authType);
};

export const checkPremiumStatus = async (): Promise<boolean> => {
  console.log('[checkPremiumStatus] Starting premium status check...');
  
  try {
    console.log('[checkPremiumStatus] Checking if user is authenticated...');
    if (!authen?.currentUser) {
      console.log('[checkPremiumStatus] No authenticated user found');
      return false;
    }
    
    console.log('[checkPremiumStatus] User is authenticated, getting ID token...');
    const token = await (authen.currentUser as any)?.getIdToken(true);
    console.log('[checkPremiumStatus] ID token obtained, length:', token?.length || 'undefined');
    
    console.log('[checkPremiumStatus] Getting decoded token result...');
    const decodedToken: any = await authen.currentUser?.getIdTokenResult();

    console.log('[checkPremiumStatus] Checking Stripe role...');
    const hasStripeRole = decodedToken?.claims?.stripeRole;
    console.log('[checkPremiumStatus] Stripe role found:', hasStripeRole);
    
    console.log('[checkPremiumStatus] Checking RevenueCat entitlements...');
    const hasRevenueCatEntitlements = decodedToken?.claims?.revenueCatEntitlements && 
                                     decodedToken?.claims?.revenueCatEntitlements?.[0];
    console.log('[checkPremiumStatus] RevenueCat entitlements:', hasRevenueCatEntitlements);
    console.log('[checkPremiumStatus] Full RevenueCat entitlements array:', decodedToken?.claims?.revenueCatEntitlements);

    // Check for immediate premium access (no date validation needed)
    if (hasStripeRole || hasRevenueCatEntitlements) {
      console.log('[checkPremiumStatus] Stripe Premium access granted via role/entitlements');
      return true;
    }

    // Check for custom claim date-based premium access
    console.log('[checkPremiumStatus] Checking Firebase custom claim date...');
    const customClaimDate = decodedToken?.claims?.stripeRoleDate || decodedToken?.claims?.premiumExpiry;
    console.log('[checkPremiumStatus] Custom claim date found:', customClaimDate);
    
    if (customClaimDate) {
      const currentDate = new Date();
      const claimExpiryDate = new Date(customClaimDate);
      const isNotExpired = currentDate <= claimExpiryDate;
      console.log('[checkPremiumStatus] Current date:', currentDate.toISOString());
      console.log('[checkPremiumStatus] Claim expiry date:', claimExpiryDate.toISOString());
      console.log('[checkPremiumStatus] Is claim not expired?', isNotExpired);
      
      if (isNotExpired) {
        console.log('[checkPremiumStatus] Stripe Premium access granted via custom claim');
        return true;
      } else {
        console.log('[checkPremiumStatus] Stripe Premium custom claim has expired');
      }
    } else {
      console.log('[checkPremiumStatus] No custom claim date found');
    }
    
    console.log('[checkPremiumStatus] Final premium status: false');
    return false;
  } catch (error) {
    console.error('[checkPremiumStatus] Error checking premium status:', error);
    return false;
  }
};

export const checkTrialExpiryAndMembership = async (userData: UserProfileData | null): Promise<TrialExpiryResult> => {
  console.log('[checkTrialExpiryAndMembership] Starting trial expiry and membership check...');
  
  const response: TrialExpiryResult = {
    expired: false,
    message: "",
    redirectToMembership: false,
    isPremium: false
  };

  if (!userData) {
    console.log('[checkTrialExpiryAndMembership] No user data provided, returning default response');
    return response;
  }

  const currentDate = new Date();
  console.log('[checkTrialExpiryAndMembership] Current date:', currentDate.toISOString());

  const september24_2025 = new Date('2025-09-24T00:00:00.000Z');
  if (currentDate < september24_2025) {
    console.log('[checkTrialExpiryAndMembership] TEMPORARY: Allowing access for everyone until September 24th, 2025');
    return response;
  }

  // Check all access conditions using OR logic
  console.log('[checkTrialExpiryAndMembership] Checking all access conditions...');

  // 1. Stripe Premium: Firebase custom claim date not expired
  console.log('[checkTrialExpiryAndMembership] Checking Stripe Premium status...');
  const isPremium = await checkPremiumStatus();
  response.isPremium = isPremium;
  console.log('[checkTrialExpiryAndMembership] Premium status result:', isPremium);

  if (isPremium) {
    console.log('[checkTrialExpiryAndMembership] Stripe Premium access granted');
    return response;
  }

  // 2. User role = 'teacher' or 'admin' (case-sensitive check)
  console.log('[checkTrialExpiryAndMembership] Checking user role...');
  console.log('[checkTrialExpiryAndMembership] User role:', userData.user_role);
  
  const isTeacherOrAdmin = userData.user_role === 'teacher' || userData.user_role === 'admin' || 
                          userData.user_role === 'Admin' || userData.user_role === 'Teacher' ||
                          userData.user_role?.toLowerCase() === 'admin' || 
                          userData.user_role?.toLowerCase() === 'teacher';
  
  if (isTeacherOrAdmin) {
    console.log('[checkTrialExpiryAndMembership] User has teacher/admin role, granting access');
    return response;
  }

  // 3. Free trial expiry > current date
  console.log('[checkTrialExpiryAndMembership] Checking free trial expiry...');
  if (userData.free_trial_expiry) {
    console.log('[checkTrialExpiryAndMembership] Free trial expiry value:', userData.free_trial_expiry);
    
    const trialExpiryDate = parseDate(userData.free_trial_expiry);
    if (trialExpiryDate) {
      console.log('[checkTrialExpiryAndMembership] Parsed trial expiry date:', trialExpiryDate.toISOString());
      console.log('[checkTrialExpiryAndMembership] Is trial expiry > current date?', trialExpiryDate > currentDate);
      
      if (trialExpiryDate > currentDate) {
        console.log('[checkTrialExpiryAndMembership] Free trial is still active, granting access');
        return response;
      } else {
        console.log('[checkTrialExpiryAndMembership] Free trial has expired');
      }
    } else {
      console.log('[checkTrialExpiryAndMembership] Failed to parse free trial expiry date');
    }
  } else {
    console.log('[checkTrialExpiryAndMembership] No free trial expiry date found');
  }

  // 4. Membership end date > current date
  console.log('[checkTrialExpiryAndMembership] Checking membership end date...');
  if (userData.membership_end_date) {
    console.log('[checkTrialExpiryAndMembership] Membership end date value:', userData.membership_end_date);
    
    const membershipExpiryDate = parseDate(userData.membership_end_date);
    if (membershipExpiryDate) {
      console.log('[checkTrialExpiryAndMembership] Parsed membership expiry date:', membershipExpiryDate.toISOString());
      console.log('[checkTrialExpiryAndMembership] Is membership expiry > current date?', membershipExpiryDate > currentDate);
      
      if (membershipExpiryDate > currentDate) {
        console.log('[checkTrialExpiryAndMembership] Membership is still active, granting access');
        return response;
      } else {
        console.log('[checkTrialExpiryAndMembership] Membership has expired');
      }
    } else {
      console.log('[checkTrialExpiryAndMembership] Failed to parse membership end date');
    }
  } else {
    console.log('[checkTrialExpiryAndMembership] No membership end date found');
  }

  // 5. Otherwise -> Block
  console.log('[checkTrialExpiryAndMembership] All access methods have expired, blocking user');
  
  // Determine the appropriate message based on auth type
  const isInstitutional = isInstitutionalAccount(userData.auth_type);
  let message: string;
  
  if (isInstitutional) {
    // Scenario 2: Clever (teacher or district) or Classlink account
    message = "Your free trial has expired. Please ask your teacher about getting a class subscription, or purchase an individual subscription for you from the Membership page";
  } else {
    // Scenario 1: Individual account (not Clever, Classlink, or Username)
    message = "Your free trial has expired. Please ask your parent to purchase a subscription from the Membership page.";
  }
  
  return {
    expired: true,
    message: message,
    redirectToMembership: true,
    isPremium: false
  };
};

export const checkTrialExpiry = async (userData: UserProfileData | null): Promise<TrialExpiryResult> => {
  console.log('[checkTrialExpiry] Called, delegating to checkTrialExpiryAndMembership...');
  return checkTrialExpiryAndMembership(userData);
};

export const getUserAccessInfo = async (userData: UserProfileData | null) => {
  console.log('[getUserAccessInfo] Starting user access info check...');
  
  if (!userData) {
    console.log('[getUserAccessInfo] No user data provided, returning BLOCKED');
    return { accessType: 'BLOCKED', reason: 'No user data' };
  }
  
  const currentDate = new Date();
  console.log('[getUserAccessInfo] Current date:', currentDate.toISOString());

  const september24_2025 = new Date('2025-09-24T00:00:00.000Z');
  if (currentDate < september24_2025) {
    console.log('[getUserAccessInfo] TEMPORARY: Allowing access for everyone until September 24th, 2025');
    return { accessType: 'TEMPORARY_BYPASS', reason: 'Temporary access until September 24th, 2025' };
  }

  // Check all access conditions using OR logic
  console.log('[getUserAccessInfo] Checking all access conditions...');

  // 1. Stripe Premium: Firebase custom claim date not expired
  console.log('[getUserAccessInfo] Checking premium status...');
  const isPremium = await checkPremiumStatus();
  console.log('[getUserAccessInfo] Premium status result:', isPremium);
  
  if (isPremium) {
    console.log('[getUserAccessInfo] User is premium, returning PREMIUM access type');
    return { accessType: 'PREMIUM', reason: 'Stripe subscription active via custom claim' };
  }

  // 2. User role = 'teacher' or 'admin' (case-sensitive check)
  console.log('[getUserAccessInfo] Checking user role...');
  console.log('[getUserAccessInfo] User role:', userData.user_role);
  
  const isTeacherOrAdmin = ['teacher', 'admin', 'district_admin', 'staff'].includes(
    (userData.user_role ?? '').toLowerCase()
  );  
  
  if (isTeacherOrAdmin) {
    console.log('[getUserAccessInfo] User has teacher/admin role, returning ROLE_BYPASS');
    return { accessType: 'ROLE_BYPASS', reason: `User role: ${userData.user_role}` };
  }

  // 3. Free trial expiry > current date
  console.log('[getUserAccessInfo] Checking free trial expiry...');
  if (userData.free_trial_expiry) {
    console.log('[getUserAccessInfo] Free trial expiry value:', userData.free_trial_expiry);
    
    const trialExpiryDate = parseDate(userData.free_trial_expiry);
    if (trialExpiryDate) {
      console.log('[getUserAccessInfo] Parsed trial expiry date:', trialExpiryDate.toISOString());
      console.log('[getUserAccessInfo] Is trial expiry > current date?', trialExpiryDate > currentDate);
      
      if (trialExpiryDate > currentDate) {
        console.log('[getUserAccessInfo] Free trial is active, returning FREE_TRIAL access type');
        return { accessType: 'FREE_TRIAL', reason: 'Trial active', expiresAt: trialExpiryDate };
      } else {
        console.log('[getUserAccessInfo] Free trial has expired');
      }
    } else {
      console.log('[getUserAccessInfo] Failed to parse free trial expiry date');
    }
  } else {
    console.log('[getUserAccessInfo] No free trial expiry date found');
  }

  // 4. Membership end date > current date
  console.log('[getUserAccessInfo] Checking membership end date...');
  if (userData.membership_end_date) {
    console.log('[getUserAccessInfo] Membership end date value:', userData.membership_end_date);
    
    const membershipExpiryDate = parseDate(userData.membership_end_date);
    if (membershipExpiryDate) {
      console.log('[getUserAccessInfo] Parsed membership expiry date:', membershipExpiryDate.toISOString());
      console.log('[getUserAccessInfo] Is membership expiry > current date?', membershipExpiryDate > currentDate);
      
      if (membershipExpiryDate > currentDate) {
        console.log('[getUserAccessInfo] Membership is active, returning INSTITUTIONAL_MEMBERSHIP access type');
        return { accessType: 'INSTITUTIONAL_MEMBERSHIP', reason: 'Membership active', expiresAt: membershipExpiryDate };
      } else {
        console.log('[getUserAccessInfo] Membership has expired');
      }
    } else {
      console.log('[getUserAccessInfo] Failed to parse membership end date');
    }
  } else {
    console.log('[getUserAccessInfo] No membership end date found');
  }

  // 5. Otherwise -> Block
  console.log('[getUserAccessInfo] No valid access method found, returning BLOCKED');
  return { accessType: 'BLOCKED', reason: 'No valid access method found' };
};
